import { useState, useEffect, useRef } from 'react';
import { Send, Trash2, Smile, MessageCircle } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';
import Navigation from '@/components/Navigation';
import { API_BASE_URL } from '@/config/api';
import { useTournament } from '@/context/TournamentContext';
import EmojiPicker from 'emoji-picker-react';

const API = API_BASE_URL;

export default function TalkSmack() {
  const [user, setUser] = useState(null);
  const [messages, setMessages] = useState([]);
  const [newMessage, setNewMessage] = useState('');
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [showEmojiPicker, setShowEmojiPicker] = useState(false);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const { selectedTournamentId } = useTournament();
  const inputRef = useRef(null);
  const emojiRef = useRef(null);

  useEffect(() => {
    const init = async () => {
      try {
        const userRes = await axios.get(`${API}/auth/me`, { withCredentials: true });
        setUser(userRes.data);
      } catch { /* ProtectedRoute handles this */ }
    };
    init();
  }, []);

  useEffect(() => {
    if (user) fetchMessages();
  }, [user, selectedTournamentId, page]);

  // Auto-refresh messages every 15 seconds
  useEffect(() => {
    if (!user) return;
    const interval = setInterval(() => fetchMessages(true), 15000);
    return () => clearInterval(interval);
  }, [user, selectedTournamentId, page]);

  // Close emoji picker on outside click
  useEffect(() => {
    const handleClick = (e) => {
      if (emojiRef.current && !emojiRef.current.contains(e.target)) {
        setShowEmojiPicker(false);
      }
    };
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, []);

  const fetchMessages = async (silent = false) => {
    try {
      if (!silent) setLoading(true);
      const params = { page, limit: 50 };
      if (selectedTournamentId) params.tournament_id = selectedTournamentId;
      const res = await axios.get(`${API}/messages`, { params, withCredentials: true });
      setMessages(res.data.messages);
      setTotalPages(res.data.pages);
    } catch {
      if (!silent) toast.error('Failed to load messages');
    } finally {
      if (!silent) setLoading(false);
    }
  };

  const handleSend = async (e) => {
    e.preventDefault();
    if (!newMessage.trim() || sending) return;

    setSending(true);
    try {
      const res = await axios.post(`${API}/messages`, {
        content: newMessage.trim(),
        tournament_id: selectedTournamentId
      }, { withCredentials: true });
      setMessages(prev => [res.data, ...prev]);
      setNewMessage('');
      setShowEmojiPicker(false);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to send message');
    } finally {
      setSending(false);
    }
  };

  const handleDelete = async (messageId) => {
    try {
      await axios.delete(`${API}/messages/${messageId}`, { withCredentials: true });
      setMessages(prev => prev.filter(m => m.message_id !== messageId));
      toast.success('Message deleted');
    } catch {
      toast.error('Failed to delete message');
    }
  };

  const onEmojiClick = (emojiData) => {
    setNewMessage(prev => prev + emojiData.emoji);
    inputRef.current?.focus();
  };

  const formatTime = (dateStr) => {
    const d = new Date(dateStr);
    const now = new Date();
    const diffMs = now - d;
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 1) return 'just now';
    if (diffMins < 60) return `${diffMins}m ago`;
    if (diffHours < 24) return `${diffHours}h ago`;
    if (diffDays < 7) return `${diffDays}d ago`;
    return d.toLocaleDateString();
  };

  if (!user) return null;

  return (
    <div className="min-h-screen bg-emerald-50">
      <Navigation user={user} />
      <div className="max-w-3xl mx-auto px-4 py-6">
        {/* Header */}
        <div data-testid="talk-smack-header" className="bg-emerald-900 text-white rounded-lg p-6 mb-6">
          <div className="flex items-center gap-3">
            <MessageCircle size={32} className="text-amber-500" />
            <div>
              <h1 className="font-bebas text-4xl tracking-wider">Talk Smack</h1>
              <p className="font-manrope text-emerald-200 text-sm">Let your rivals know what's coming</p>
            </div>
          </div>
        </div>

        {/* Message Input */}
        <form onSubmit={handleSend} className="bg-white rounded-lg shadow-sm border border-emerald-200 p-4 mb-6">
          <div className="flex items-end gap-3">
            <div className="flex-1 relative">
              <textarea
                ref={inputRef}
                data-testid="message-input"
                value={newMessage}
                onChange={(e) => setNewMessage(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' && !e.shiftKey) {
                    e.preventDefault();
                    handleSend(e);
                  }
                }}
                placeholder="Drop some smack..."
                maxLength={500}
                rows={2}
                className="w-full resize-none rounded-lg border border-gray-200 px-4 py-3 font-manrope text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-transparent"
              />
              <span className="absolute bottom-2 right-3 text-xs text-gray-400 font-manrope">
                {newMessage.length}/500
              </span>
            </div>
            <div className="flex gap-2 pb-1">
              <div className="relative" ref={emojiRef}>
                <button
                  type="button"
                  data-testid="emoji-picker-button"
                  onClick={() => setShowEmojiPicker(!showEmojiPicker)}
                  className="p-3 rounded-lg bg-gray-100 hover:bg-gray-200 transition-colors"
                >
                  <Smile size={20} className="text-gray-600" />
                </button>
                {showEmojiPicker && (
                  <div className="absolute bottom-14 right-0 z-50">
                    <EmojiPicker
                      onEmojiClick={onEmojiClick}
                      width={320}
                      height={400}
                      searchPlaceHolder="Search emoji..."
                      previewConfig={{ showPreview: false }}
                    />
                  </div>
                )}
              </div>
              <button
                type="submit"
                data-testid="send-message-button"
                disabled={!newMessage.trim() || sending}
                className="p-3 rounded-lg bg-emerald-700 hover:bg-emerald-800 disabled:bg-gray-300 disabled:cursor-not-allowed transition-colors"
              >
                <Send size={20} className="text-white" />
              </button>
            </div>
          </div>
        </form>

        {/* Messages */}
        <div data-testid="messages-list" className="space-y-3">
          {loading ? (
            <div className="text-center py-12">
              <div className="animate-spin rounded-full h-10 w-10 border-4 border-emerald-600 border-t-transparent mx-auto mb-3"></div>
              <p className="font-manrope text-gray-500">Loading messages...</p>
            </div>
          ) : messages.length === 0 ? (
            <div className="text-center py-16 bg-white rounded-lg border border-dashed border-emerald-300">
              <MessageCircle size={48} className="mx-auto text-emerald-300 mb-3" />
              <p className="font-bebas text-2xl text-emerald-800 tracking-wider">No smack yet</p>
              <p className="font-manrope text-gray-500 text-sm">Be the first to talk some trash!</p>
            </div>
          ) : (
            messages.map((msg) => {
              const isOwn = msg.user_id === user.user_id;
              const canDelete = isOwn || user.is_admin;
              return (
                <div
                  key={msg.message_id}
                  data-testid={`message-${msg.message_id}`}
                  className={`bg-white rounded-lg border p-4 transition-all hover:shadow-sm ${
                    isOwn ? 'border-emerald-300 bg-emerald-50/50' : 'border-gray-200'
                  }`}
                >
                  <div className="flex items-start gap-3">
                    {msg.user_picture ? (
                      <img
                        src={msg.user_picture}
                        alt={msg.user_name}
                        className="w-10 h-10 rounded-full border-2 border-emerald-600 flex-shrink-0"
                      />
                    ) : (
                      <div className="w-10 h-10 rounded-full bg-emerald-700 flex items-center justify-center flex-shrink-0">
                        <span className="text-white font-bebas text-lg">
                          {msg.user_name?.charAt(0)?.toUpperCase()}
                        </span>
                      </div>
                    )}
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-1">
                        <span className="font-manrope font-semibold text-sm text-emerald-900">
                          {msg.user_name}
                        </span>
                        {isOwn && (
                          <span className="text-xs bg-emerald-100 text-emerald-700 px-2 py-0.5 rounded-full font-manrope">
                            you
                          </span>
                        )}
                        <span className="text-xs text-gray-400 font-manrope">
                          {formatTime(msg.created_at)}
                        </span>
                      </div>
                      <p className="font-manrope text-sm text-gray-800 whitespace-pre-wrap break-words">
                        {msg.content}
                      </p>
                    </div>
                    {canDelete && (
                      <button
                        data-testid={`delete-message-${msg.message_id}`}
                        onClick={() => handleDelete(msg.message_id)}
                        className="p-1.5 rounded hover:bg-red-50 text-gray-400 hover:text-red-500 transition-colors flex-shrink-0"
                        title="Delete message"
                      >
                        <Trash2 size={16} />
                      </button>
                    )}
                  </div>
                </div>
              );
            })
          )}
        </div>

        {/* Pagination */}
        {totalPages > 1 && (
          <div data-testid="messages-pagination" className="flex justify-center gap-2 mt-6">
            <button
              onClick={() => setPage(p => Math.max(1, p - 1))}
              disabled={page === 1}
              className="px-4 py-2 rounded-lg bg-white border border-gray-200 font-manrope text-sm disabled:opacity-50 hover:bg-gray-50 transition-colors"
            >
              Newer
            </button>
            <span className="px-4 py-2 font-manrope text-sm text-gray-600">
              Page {page} of {totalPages}
            </span>
            <button
              onClick={() => setPage(p => Math.min(totalPages, p + 1))}
              disabled={page === totalPages}
              className="px-4 py-2 rounded-lg bg-white border border-gray-200 font-manrope text-sm disabled:opacity-50 hover:bg-gray-50 transition-colors"
            >
              Older
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
